// Initialize API config BEFORE importing mehirApi
import './api-config';

// Use the shared mehirApi from libs/api
import { mehirApi } from '@mehir/api/api';

// Re-export as backofficeApi for store compatibility
export const backofficeApi = mehirApi;

export type ApiErrorKind =
  | 'validation' // 400 / 422 — the request itself was rejected
  | 'unauthorized' // 401
  | 'forbidden' // 403
  | 'notFound' // 404
  | 'server' // 5xx
  | 'network' // request never completed
  | 'unknown';

export interface ParsedApiError {
  kind: ApiErrorKind;
  status: number | null;
  /** Server-supplied message, when there is one worth showing. */
  message: string | null;
}

/** One entry of this backend's `errors` array. */
interface BackendError {
  description?: string | null;
  /** 'validation' | 'failure' | 'conflict' | … — the backend's own ErrorType. */
  type?: string | null;
}

/** RFC 7807 problem details, plus the shapes this backend also returns. */
interface ProblemDetails {
  detail?: string | null;
  title?: string | null;
  message?: string | null;
  errorMessages?: (string | null)[] | null;
  /**
   * Either this backend's array of typed errors, or ASP.NET's model-state
   * dictionary of field → messages.
   */
  errors?: BackendError[] | Record<string, string[] | undefined> | null;
}

/**
 * True when the payload describes something the user can fix. The backend tags
 * its own unhandled exceptions as `failure` and hands back the raw .NET message
 * ("No service for type 'MediatR.IRequestHandler`2[…]' has been registered"),
 * which is worth logging and useless in a toast.
 */
const isUserFacingProblem = (body: unknown): boolean => {
  if (!body || typeof body !== 'object') return true;
  const errors = (body as ProblemDetails).errors;
  if (!Array.isArray(errors) || errors.length === 0) return true;
  return errors.some((entry) => (entry?.type ?? '').toLowerCase() === 'validation');
};

const firstString = (...values: (string | null | undefined)[]): string | null =>
  values.find((value): value is string => typeof value === 'string' && value.trim().length > 0) ??
  null;

const messageFromBody = (body: unknown): string | null => {
  if (typeof body === 'string') return body.trim() || null;
  if (!body || typeof body !== 'object') return null;

  const problem = body as ProblemDetails;

  if (Array.isArray(problem.errors)) {
    // This backend's typed errors — only the validation ones say anything a
    // user can act on.
    const validation = problem.errors.find(
      (entry) => (entry?.type ?? '').toLowerCase() === 'validation'
    );
    if (validation) return firstString(validation.description);
    return null;
  }

  // ASP.NET model-state validation: { errors: { Field: ["msg", ...] } }
  const fieldErrors = problem.errors
    ? Object.values(problem.errors).flatMap((messages) => messages ?? [])
    : [];

  return firstString(
    problem.detail,
    problem.message,
    fieldErrors[0],
    problem.errorMessages?.[0] ?? null,
    problem.title
  );
};

const kindFor = (status: number): ApiErrorKind => {
  if (status === 400 || status === 422) return 'validation';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status >= 500) return 'server';
  return 'unknown';
};

/**
 * Normalises an RTK Query rejection.
 *
 * The previous version reported every failure it could not classify as a 500,
 * so a validation error and a dropped connection both surfaced as "Sunucu
 * Hatası" — and 400s, which this backend uses for validation *and* for its own
 * unhandled exceptions, were dropped entirely.
 */
export const parseError = (error: unknown): ParsedApiError => {
  const candidate = error as { status?: number | string; data?: unknown; error?: string };

  if (typeof candidate?.status === 'number') {
    const kind = kindFor(candidate.status);
    // A 400 the backend tagged as its own failure is a server fault wearing a
    // client-error status code; report it as one rather than blaming the user.
    const userFacing = isUserFacingProblem(candidate.data);
    return {
      kind: kind === 'validation' && !userFacing ? 'server' : kind,
      status: candidate.status,
      message: messageFromBody(candidate.data),
    };
  }

  // FETCH_ERROR / TIMEOUT_ERROR — the request never reached the server.
  if (candidate?.status === 'FETCH_ERROR' || candidate?.status === 'TIMEOUT_ERROR') {
    return { kind: 'network', status: null, message: null };
  }

  // PARSING_ERROR carries the original HTTP status.
  if (candidate?.status === 'PARSING_ERROR') {
    return { kind: 'server', status: null, message: null };
  }

  return { kind: 'unknown', status: null, message: null };
};
